import React from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { Tooltip, EPIC_LEVEL } from '@atlassian/jira-portfolio-3-common';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import BasicPopupSelect from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/dependencies/popup-select/basic';
import {
	GroupBy,
	ROLLUP_BY_OPTION,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/dependencies/types';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';
import type { Props } from './types';

function GroupByDropdown({
	rollupBy,
	groupBy,
	hierarchy,
	isOpen = false,
	setGroupBy,
	onFilterChange,
}: Props) {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const noneOption = {
		label: intl.formatMessage(commonMessages.none),
		value: GroupBy.NONE,
	};
	const teamOption = {
		label: intl.formatMessage(commonMessages.team),
		value: GroupBy.TEAM,
	};
	const projectOption = {
		label: intl.formatMessage(commonMessages.project),
		value: GroupBy.PROJECT,
	};
	const sprintOption = {
		label: intl.formatMessage(commonMessages.sprint),
		value: GroupBy.SPRINT,
	};

	const epicHierarchy =
		hierarchy && hierarchy.length
			? hierarchy.find((hierarchyLevel) => hierarchyLevel.level === EPIC_LEVEL)
			: null;
	const epicOption = {
		label: epicHierarchy ? epicHierarchy.title : '',
		value: GroupBy.EPIC_LEVEL,
	};

	let value: OptionType = noneOption;

	switch (groupBy) {
		case GroupBy.TEAM:
			value = teamOption;
			break;
		case GroupBy.PROJECT:
			value = projectOption;
			break;
		case GroupBy.EPIC_LEVEL:
			value = epicOption;
			break;
		case GroupBy.SPRINT:
			value = sprintOption;
			break;
		default:
			break;
	}

	const options = [noneOption, epicOption, projectOption, teamOption, sprintOption];

	const isDisabled = rollupBy === ROLLUP_BY_OPTION.TEAM;
	const select = (
		<BasicPopupSelect
			label={intl.formatMessage(commonMessages.groupBy)}
			defaultIsOpen={isOpen}
			options={options}
			emptyValues={[GroupBy.NONE]}
			isDisabled={isDisabled}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onChange={(e: any) => {
				if (e && e.value) {
					setGroupBy(e.value.toString());
				} else {
					setGroupBy(GroupBy.NONE);
				}
				onFilterChange();

				const [actionSubject, action] =
					PRODUCT_ANALYTICS_EVENT_NAMES.UPDATED_DEPENDENCY_GROUP_DROPDOWN.split(' ');
				fireUIAnalytics(
					createAnalyticsEvent({
						action,
						actionSubject,
					}),
					{
						group: e && e.value ? e.value.toString() : GroupBy.NONE,
					},
				);
			}}
			value={value}
			testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.filters.group-by.group-by"
		/>
	);
	if (isDisabled) {
		return (
			<Tooltip content={intl.formatMessage(messages.restrictedGroupByWithRollupToTeam)}>
				{select}
			</Tooltip>
		);
	}

	return select;
}

export default GroupByDropdown;
